// BUILD-046 section B (Owner direction of 2026-10-21): the pure parts of the active-filter row - the match count and the Department options.
//
// MATCH COUNT. "3 of 42 loaded tasks match": it says tasks or requests (Assigned by Me lists requests, the other tabs list tasks) and says "loaded" whenever the list is not whole, so it never claims to count rows that were
// not read. Matching is the rows left after every narrowing criterion and the search; the total is the rows the view loaded for the chosen Status.
// DEPARTMENT OPTIONS. The department of a row is the value the directory gave for its person (the pilot directory: Accounting, Field, Design, Operations...). The options are the distinct values on the LOADED rows, so a choice always
// matches the rows it names; there is no list typed into the page and no mapping is invented for a row that carries no department (an unclaimed group task has none, so a Department choice leaves it out).
//
// Pure module: no React, no I/O, no imports. Erasable TypeScript only.

export type CountNoun = "task" | "request";

export interface MatchCountInput {
  matching: number;
  total: number;
  /** False for a list read page by page (only the loaded rows were counted). */
  complete: boolean;
  noun: CountNoun;
}

function whole(n: unknown): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * The concise result count of the active-filter row (Owner correction 2026-10-24, item 4): "Showing 1 of 4", and "Showing 1 of 4 loaded" while the list is read page by page (it never claims to count
 * rows that were not read). The longer sentence that names the rows is `describeMatchCountLong`, kept as the count's hover text and accessible detail.
 */
export function describeMatchCount(input: MatchCountInput): string {
  const matching = whole(input.matching);
  const total = Math.max(whole(input.total), matching);
  return "Showing " + matching + " of " + total + (input.complete ? "" : " loaded");
}

/** The full sentence ("3 of 42 loaded tasks match"): it says tasks or requests and "loaded" whenever the list is not whole. */
export function describeMatchCountLong(input: MatchCountInput): string {
  const matching = whole(input.matching);
  const total = Math.max(whole(input.total), matching);
  const noun = total === 1 ? input.noun : input.noun + "s";
  return matching + " of " + total + " " + (input.complete ? "" : "loaded ") + noun + (matching === 1 ? " matches" : " match");
}

function departmentOf(row: unknown): string {
  if (row === null || typeof row !== "object") return "";
  const value = (row as { department?: unknown }).department;
  return typeof value === "string" ? value.trim() : "";
}

/** The distinct departments on the loaded rows, in name order, plus the chosen one when no loaded row carries it any more (so the choice stays visible and can be changed). */
export function distinctDepartments(rowSets: readonly (readonly unknown[])[], current: string = ""): string[] {
  const found = new Set<string>();
  for (const rows of Array.isArray(rowSets) ? rowSets : []) for (const row of Array.isArray(rows) ? rows : []) {
    const department = departmentOf(row);
    if (department !== "") found.add(department);
  }
  const chosen = typeof current === "string" ? current.trim() : "";
  if (chosen !== "") found.add(chosen);
  return [...found].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }) || (a < b ? -1 : a > b ? 1 : 0));
}

export const DEPARTMENT_FILTER_LABEL = "Department";
export const ANY_DEPARTMENT_LABEL = "Any department";

/** The badge for a chosen department, or null for none. */
export function describeDepartmentBadge(department: unknown): { text: string; removeLabel: string } | null {
  const value = typeof department === "string" ? department.trim() : "";
  return value === "" ? null : { text: DEPARTMENT_FILTER_LABEL + ": " + value, removeLabel: "Remove " + DEPARTMENT_FILTER_LABEL + " filter" };
}

export const QUICK_FILTER_BADGE_LABEL = "Filter";

/** The badge for a My Tasks quick filter that is not already shown as the Status (Overdue and Follow-up due are Status choices, so they have no badge). */
export function describeQuickFilterBadge(quick: unknown, labelOf: (value: string) => string): { text: string; removeLabel: string } | null {
  if (typeof quick !== "string" || quick === "all" || quick === "overdue" || quick === "follow-up-due") return null;
  return { text: QUICK_FILTER_BADGE_LABEL + ": " + labelOf(quick), removeLabel: "Remove " + QUICK_FILTER_BADGE_LABEL + " filter" };
}

export const CLEAR_FILTERS_LABEL = "Clear filters";

